import { apiFetch } from "./client";

export type PoolProvider = "anthropic" | "openai-codex";

export interface ProviderAccount {
  id: string;
  provider: PoolProvider;
  label: string | null;
  status: "pending" | "active" | "disabled";
  accountId: string | null;
  tokenPreview: string | null;
  hasRefreshToken: boolean;
  expires: number | null;
  cooling: boolean;
  cooldownUntil: number | null;
  lastError: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  /** 2 = overflow; null = primary. */
  priority: number | null;
  /** Upstream model names this account may serve; null = all. */
  allowedModels: string[] | null;
  /** Who the account is signed in as; null until the first lookup. */
  identity: {
    email: string | null;
    name: string | null;
    organization: string | null;
    plan: string | null;
    checkedAt: string;
  } | null;
  plan: string | null;
}

export interface UsageAgent {
  agentId: string | null;
  agentSlug: string;
  name: string;
  turns: number;
  inputTokens: number;
  outputTokens: number;
  errors: number;
  background: number;
  lastTs: string | null;
  providers: Array<{ provider: string; turns: number; inputTokens: number; outputTokens: number }>;
}

export interface UsageResponse {
  days: number;
  agents: UsageAgent[];
  daily: Array<{ day: string; turns: number; inputTokens: number; outputTokens: number }>;
}

export const providerAccountsApi = {
  list: (tenantId: string) =>
    apiFetch<{ accounts: ProviderAccount[] }>(`/admin/tenants/${tenantId}/provider-accounts`),

  start: (tenantId: string, provider: PoolProvider, label?: string) =>
    apiFetch<{ accountId: string; authUrl: string; provider: PoolProvider }>(
      `/admin/tenants/${tenantId}/provider-accounts/start`,
      { method: "POST", body: JSON.stringify({ provider, label }) },
    ),

  complete: (tenantId: string, accountId: string, callbackUrl: string) =>
    apiFetch<{ account: ProviderAccount }>(
      `/admin/tenants/${tenantId}/provider-accounts/${accountId}/complete`,
      { method: "POST", body: JSON.stringify({ callbackUrl }) },
    ),

  update: (
    tenantId: string,
    accountId: string,
    data: {
      label?: string;
      status?: "active" | "disabled";
      clearCooldown?: boolean;
      priority?: 1 | 2 | null;
      allowedModels?: string[] | null;
    },
  ) =>
    apiFetch<{ account: ProviderAccount }>(
      `/admin/tenants/${tenantId}/provider-accounts/${accountId}`,
      { method: "PATCH", body: JSON.stringify(data) },
    ),

  remove: (tenantId: string, accountId: string) =>
    apiFetch<{ success: boolean }>(`/admin/tenants/${tenantId}/provider-accounts/${accountId}`, {
      method: "DELETE",
    }),

  importLegacy: (tenantId: string) =>
    apiFetch<{ imported: number }>(`/admin/tenants/${tenantId}/provider-accounts/import-legacy`, {
      method: "POST",
    }),

  usage: (tenantId: string, days = 7) =>
    apiFetch<UsageResponse>(`/admin/tenants/${tenantId}/usage?days=${days}`),
};
